import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";

const navLinks = [
  { label: "Who We Are", to: "/who-we-are", testid: "nav-who-we-are" },
  { label: "Directors", to: "/directors", testid: "nav-directors" },
  { label: "Projects", to: "/projects", testid: "nav-projects" },
  { label: "Connect", to: "/connect", testid: "nav-connect" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const darkHero = location.pathname === "/" || location.pathname === "/who-we-are";
  const onDark = darkHero && !scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-sp-navy/10 shadow-[0_4px_24px_rgba(40,78,120,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Logo variant={onDark ? "dark" : "light"} />

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`sp-link-underline font-sans text-sm font-medium tracking-wide transition-colors duration-300 flex items-center gap-1.5 ${
                onDark
                  ? "text-white/85 hover:text-white"
                  : location.pathname === l.to
                  ? "text-sp-navy"
                  : "text-sp-deep/75 hover:text-sp-navy"
              }`}
            >
              {l.label === "Projects" && <Lock className="w-3 h-3" strokeWidth={1.75} />}
              {l.label}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin-console"
              className={`sp-link-underline font-sans text-sm font-medium tracking-wide transition-colors duration-300 ${
                onDark ? "text-white/85 hover:text-white" : "text-sp-deep/75 hover:text-sp-navy"
              }`}
            >
              Console
            </Link>
          )}
          <button
            data-testid="nav-signout"
            onClick={() => { logout(); navigate("/"); }}
            className={`rounded-full px-5 py-2 text-sm transition-colors duration-300 ${
              onDark
                ? "border border-white/30 text-white hover:bg-white/10"
                : "border border-sp-navy/30 text-sp-navy hover:bg-sp-navy/5"
            }`}
          >
            Sign out
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden ${onDark ? "text-white" : "text-sp-deep"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-sp-navy/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  className="font-sans text-base text-sp-deep flex items-center gap-2"
                >
                  {l.label === "Projects" && <Lock className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  {l.label}
                </Link>
              ))}
              {user && user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-console-mobile" className="font-sans text-base text-sp-deep">
                  Console
                </Link>
              )}
              <button
                data-testid="nav-signout-mobile"
                onClick={() => { logout(); navigate("/"); }}
                className="text-left font-sans text-base text-sp-navy"
              >
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
